import { useState, useMemo } from "react";
import { GstReturnsView } from "./GstReturnsView";

// ─── helpers ────────────────────────────────────────────────────────────────
const inr = (n: number) => n === 0 ? "₹0" : "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n));
const uid = () => Math.random().toString(36).slice(2, 8);

// ─── Types ───────────────────────────────────────────────────────────────────
type TaxModule = "dashboard" | "itr" | "tds" | "gst";
type ItrStep = "personal" | "income" | "deductions" | "summary";
type TdsTab = "deductor" | "deductee" | "challan" | "deduction";
type RegimeType = "old" | "new";
type VerificationMode = "otp" | "evc" | "net-banking" | "dsc" | null;

interface PersonalInfo {
  name: string; pan: string; dob: string; email: string; mobile: string;
  aadhaar: string; address: string; city: string; state: string; pincode: string;
  bankAccount: string; ifsc: string; bankName: string;
  residentialStatus: "resident" | "nri";
}

interface IncomeData {
  salary: number; form16Uploaded: boolean;
  interestSavings: number; interestFd: number;
  capitalGainsStcg: number; capitalGainsLtcg: number;
  housePropertyRent: number; housePropertyInterest: number;
  businessIncome: number; cryptoIncome: number;
  otherIncome: number;
}

interface DeductionData {
  sec80C: number; sec80D: number; sec80G: number; sec80E: number;
  sec80TTA: number; sec80CCD1B: number; sec80EEA: number;
  tdsCredited: number; tcsCredits: number; advanceTax: number;
  selfAssessmentTax: number;
}

interface TdsDeductor {
  tan: string; pan: string; name: string; type: string;
  address: string; city: string; state: string; pincode: string;
  email: string; phone: string;
  responsibleName: string; responsibleDesignation: string;
}

interface TdsDeductee {
  id: string; name: string; pan: string; type: string; panStatus: string;
}

interface TdsChallan {
  id: string; date: string; section: string; tax: number;
  interest: number; fee: number; other: number; total: number;
  bsrCode: string; serialNo: string; verificationResult: string;
}

interface TdsDeduction {
  id: string; deducteeName: string; deducteePAN: string;
  amountPaid: number; paidDate: string; deductionDate: string;
  section: string; taxRate: number; taxDeducted: number;
  challanId: string; challanManual?: boolean;
}

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// Section → Chapter mapping under the Income Tax Act, 2025 (renumbered from the 1961 Act's 194-series).
// Kept as a lookup so TDS forms can surface both the familiar 194-series label and the 2025 Act clause.
const SECTION_393_MAP: Record<string, string> = {
  "192": "393(1) — Salaries",
  "194A": "393(9) — Interest other than securities",
  "194B": "393(15) — Winnings from lottery/games",
  "194C": "393(10) — Payments to contractors",
  "194H": "393(12) — Commission or brokerage",
  "194I": "393(13) — Rent",
  "194J": "393(11) — Professional/technical fees",
  "194Q": "393(19) — Purchase of goods",
};

// ─── Seed data ───────────────────────────────────────────────────────────────
const SEED_PERSONAL: PersonalInfo = {
  name: "Raghavendra K.", pan: "AABCR1234M", dob: "1993-06-15",
  email: "rk@vaarahi.in", mobile: "9901234567", aadhaar: "XXXX XXXX 4567",
  address: "#12, 3rd Cross, Whitefield", city: "Bangalore", state: "Karnataka", pincode: "560066",
  bankAccount: "XXXXXXXXX3456", ifsc: "HDFC0001234", bankName: "HDFC Bank",
  residentialStatus: "resident"
};
const SEED_INCOME: IncomeData = {
  salary: 1200000, form16Uploaded: true,
  interestSavings: 8000, interestFd: 45000,
  capitalGainsStcg: 0, capitalGainsLtcg: 0,
  housePropertyRent: 0, housePropertyInterest: 0,
  businessIncome: 0, cryptoIncome: 0, otherIncome: 0
};
const SEED_DEDUCTIONS: DeductionData = {
  sec80C: 150000, sec80D: 25000, sec80G: 0, sec80E: 0,
  sec80TTA: 8000, sec80CCD1B: 50000, sec80EEA: 0,
  tdsCredited: 120000, tcsCredits: 0, advanceTax: 0, selfAssessmentTax: 0
};

// ─── Shared client roster (same firm clients across ITR / TDS / GST) ─────────
interface TaxClient { id: string; name: string; pan: string; tan?: string; gstin?: string; city: string; state: string; contactPerson: string; mobile?: string; }
const TAX_CLIENTS: TaxClient[] = [
  { id: "1", name: "C H Infra structures", pan: "AAQFC0907Q", tan: "BLRC12345D", gstin: "29AAQFC0907Q1ZT", city: "Bangalore", state: "Karnataka", contactPerson: "Pullappa Chakali" },
  { id: "2", name: "Global Care Logistics", pan: "AQTPP7039D", tan: "BLRG23456E", gstin: "29AQTPP7039D1Z9", city: "Bangalore", state: "Karnataka", contactPerson: "Devaraj Prakash" },
  { id: "3", name: "Global Infra Conmix", pan: "ABCFG5263A", tan: "BLRG34567F", gstin: "29ABCFG5263A1ZN", city: "Bangalore", state: "Karnataka", contactPerson: "Balakrishna Samidisetty" },
  { id: "4", name: "Haronics Consumer Products LLP", pan: "AAOFH3011L", tan: "BLRH45678G", gstin: "29AAOFH3011L1Z7", city: "Bangalore", state: "Karnataka", contactPerson: "Vasanthakumar Sannenahalli" },
  { id: "5", name: "Icon Infra", pan: "AALFI5968E", tan: "BLRI56789H", gstin: "29AALFI5968E1ZU", city: "Bangalore", state: "Karnataka", contactPerson: "Raju Ranjith Sherigar" },
  { id: "6", name: "Initiumnet Technology Private Limited", pan: "AAICI5339K", tan: "BLRI67890I", gstin: "29AAICI5339K1Z1", city: "Bangalore", state: "Karnataka", contactPerson: "Santosh Kumar Padhy" },
  { id: "7", name: "MDF Enterprises", pan: "DDSPK6142P", tan: "BLRM78901J", gstin: "29DDSPK6142P1ZK", city: "Bangalore", state: "Karnataka", contactPerson: "Subhanasab Khadaranavar" },
  { id: "8", name: "SRS Infrastructures", pan: "AFWFS7459G", tan: "BLRS89012K", gstin: "29AFWFS7459G1ZX", city: "Bangalore", state: "Karnataka", contactPerson: "Devarahally Muniyappa Suresh" },
  { id: "9", name: "Unitech Concrete", pan: "AAFFU0397M", tan: "BLRU90123L", gstin: "29AAFFU0397M2ZH", city: "Bangalore", state: "Karnataka", contactPerson: "Chinnakotla Ramesh Babu" },
  { id: "10", name: "Vaarahi Concretes", pan: "BNNPP0141C", tan: "BLRV01234M", gstin: "29BNNPP0141C1Z5", city: "Bangalore", state: "Karnataka", contactPerson: "Pullappa Chakali" },
];
const AY_OPTIONS = ["2024-25", "2025-26", "2026-27"];
const TDS_FY_OPTIONS = ["2024-25", "2025-26", "2026-27"];

// Generic client picker, reused by ITR and TDS (GST has its own richer version in GstReturnsView).
function TaxClientList({ title, onOpen, onBack }: { title: string; onOpen: (c: TaxClient) => void; onBack: () => void }) {
  return (
    <div className="tp-tds-shell">
      <div className="tp-tds-topbar">
        <div className="tp-tds-topbar-left">
          <button className="tp-back-link" onClick={onBack} type="button">← TaxPilot</button>
          <div className="tp-tds-form-selector"><span>Module :</span><strong>{title} — Client List</strong></div>
        </div>
        <div className="tp-tds-topbar-right">
          <button className="tp-btn-secondary sm" type="button">Bulk Import/Export</button>
          <button className="tp-btn-primary sm" type="button">+ Add New</button>
        </div>
      </div>
      <div className="gst-client-list-wrap">
        <table className="gst-client-table">
          <thead><tr><th>S.No</th><th>Client Name</th><th>PAN</th><th>City</th><th>State</th><th>Contact Person</th><th>Mobile</th></tr></thead>
          <tbody>
            {TAX_CLIENTS.map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td><button className="gst-client-name-link" onClick={() => onOpen(c)} type="button">{c.name}</button></td>
                <td className="gst-mono">{c.pan}</td>
                <td>{c.city}</td>
                <td>{c.state}</td>
                <td>{c.contactPerson}</td>
                <td>{c.mobile || <span className="tp-muted">Enter mobile</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Generic period picker modal — pass whichever fields the module needs.
function TaxPeriodModal({ client, fields, onGo, onExit }: {
  client: TaxClient;
  fields: Array<{ key: string; label: string; value: string; options: string[]; onChange: (v: string) => void }>;
  onGo: () => void; onExit: () => void;
}) {
  return (
    <div className="tp-modal-backdrop" onClick={onExit}>
      <div className="tp-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <div className="tp-modal-title-row"><div className="tp-modal-icon">📅</div><div><h3>Select Filing Period</h3></div></div>
          <button className="tp-modal-close" onClick={onExit} type="button">✕</button>
        </div>
        <div className="tp-modal-body">
          <div className="gst-period-row"><span>Client Name</span><strong>{client.name}</strong></div>
          <div className="gst-period-row"><span>PAN</span><strong>{client.pan}</strong></div>
          {client.tan && <div className="gst-period-row"><span>TAN</span><strong>{client.tan}</strong></div>}
          {fields.map(f => (
            <div className="tp-form-field" style={{ marginTop: 12 }} key={f.key}>
              <label>{f.label}</label>
              <select className="tp-input" value={f.value} onChange={e => f.onChange(e.target.value)}>
                {f.options.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div className="tp-modal-actions">
            <button className="tp-btn-secondary" onClick={onExit} type="button">Exit</button>
            <button className="tp-btn-primary" onClick={onGo} type="button">Go →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ITR client flow (client list → period → wizard) ──────────────────────────
function ItrClientFlow({ onBack }: { onBack: () => void }) {
  const [pendingClient, setPendingClient] = useState<TaxClient | null>(null);
  const [ay, setAy] = useState("2026-27");
  const [active, setActive] = useState<{ client: TaxClient; ay: string } | null>(null);

  if (!active) {
    return (
      <>
        <TaxClientList title="ITR Filing" onOpen={setPendingClient} onBack={onBack} />
        {pendingClient && (
          <TaxPeriodModal
            client={pendingClient}
            fields={[{ key: "ay", label: "Assessment Year", value: ay, options: AY_OPTIONS, onChange: setAy }]}
            onGo={() => { setActive({ client: pendingClient, ay }); setPendingClient(null); }}
            onExit={() => setPendingClient(null)}
          />
        )}
      </>
    );
  }
  return <ItrWizard client={active.client} ay={active.ay} onBack={() => setActive(null)} />;
}

// ─── TDS client flow (client list → period → return) ──────────────────────────
function TdsClientFlow({ onBack }: { onBack: () => void }) {
  const [pendingClient, setPendingClient] = useState<TaxClient | null>(null);
  const [fy, setFy] = useState("2025-26");
  const [active, setActive] = useState<{ client: TaxClient; fy: string } | null>(null);

  if (!active) {
    return (
      <>
        <TaxClientList title="TDS / TCS Return" onOpen={setPendingClient} onBack={onBack} />
        {pendingClient && (
          <TaxPeriodModal
            client={pendingClient}
            fields={[{ key: "fy", label: "Financial Year", value: fy, options: TDS_FY_OPTIONS, onChange: setFy }]}
            onGo={() => { setActive({ client: pendingClient, fy }); setPendingClient(null); }}
            onExit={() => setPendingClient(null)}
          />
        )}
      </>
    );
  }
  return <TdsReturn client={active.client} fy={active.fy} onBack={() => setActive(null)} />;
}

// ─── Tax computation ─────────────────────────────────────────────────────────
function computeTax(income: IncomeData, deductions: DeductionData, regime: RegimeType) {
  const gross = income.salary + income.interestSavings + income.interestFd +
    income.capitalGainsStcg + income.capitalGainsLtcg + income.housePropertyRent +
    income.businessIncome + income.cryptoIncome + income.otherIncome;

  let taxableIncome = gross;
  let totalDeductions = 0;

  if (regime === "old") {
    const standardDeduction = Math.min(50000, income.salary);
    const deductionTotal = deductions.sec80C + deductions.sec80D + deductions.sec80G +
      deductions.sec80E + deductions.sec80TTA + deductions.sec80CCD1B + deductions.sec80EEA;
    totalDeductions = standardDeduction + Math.min(deductionTotal, 300000);
    taxableIncome = Math.max(0, gross - totalDeductions);
  } else {
    // New regime: only standard deduction of 75000
    totalDeductions = Math.min(75000, income.salary);
    taxableIncome = Math.max(0, gross - totalDeductions);
  }

  let tax = 0;
  if (regime === "new") {
    // New regime slabs FY 2026-27
    if (taxableIncome <= 400000) tax = 0;
    else if (taxableIncome <= 800000) tax = (taxableIncome - 400000) * 0.05;
    else if (taxableIncome <= 1200000) tax = 20000 + (taxableIncome - 800000) * 0.10;
    else if (taxableIncome <= 1600000) tax = 60000 + (taxableIncome - 1200000) * 0.15;
    else if (taxableIncome <= 2000000) tax = 120000 + (taxableIncome - 1600000) * 0.20;
    else if (taxableIncome <= 2400000) tax = 200000 + (taxableIncome - 2000000) * 0.25;
    else tax = 300000 + (taxableIncome - 2400000) * 0.30;
    // Rebate 87A for income up to 12L
    if (taxableIncome <= 1200000) tax = 0;
  } else {
    // Old regime slabs
    if (taxableIncome <= 250000) tax = 0;
    else if (taxableIncome <= 500000) tax = (taxableIncome - 250000) * 0.05;
    else if (taxableIncome <= 1000000) tax = 12500 + (taxableIncome - 500000) * 0.20;
    else tax = 112500 + (taxableIncome - 1000000) * 0.30;
    // Rebate 87A
    if (taxableIncome <= 500000) tax = 0;
  }

  const cess = tax * 0.04;
  const totalTax = tax + cess;
  const taxPaid = deductions.tdsCredited + deductions.tcsCredits + deductions.advanceTax + deductions.selfAssessmentTax;
  const refundOrDue = taxPaid - totalTax;

  return { gross, taxableIncome, totalDeductions, tax, cess, totalTax, taxPaid, refundOrDue };
}

// ─── OTP / EVC Verification modal ───────────────────────────────────────────
export function VerificationModal({ onClose, onSuccess, title, subtitle, ackPrefix, successHeadline, successBody }: { onClose: () => void; onSuccess: () => void; title?: string; subtitle?: string; ackPrefix?: string; successHeadline?: string; successBody?: string }) {
  const [mode, setMode] = useState<VerificationMode>(null);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"select" | "verify" | "success">("select");
  const [loading, setLoading] = useState(false);
  const [bankAcc, setBankAcc] = useState("");

  const verify = async () => {
    if (!otp && mode !== "net-banking") return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setStep("success");
    setTimeout(onSuccess, 1800);
  };

  const MODES = [
    { key: "otp" as VerificationMode, icon: "📱", title: "OTP via Aadhaar", desc: "Verify using OTP sent to your Aadhaar-linked mobile", recommended: true },
    { key: "evc" as VerificationMode, icon: "🏦", title: "EVC via Bank Account", desc: "Electronic Verification Code from pre-validated bank account" },
    { key: "net-banking" as VerificationMode, icon: "🔐", title: "Net Banking", desc: "Login to your bank and verify on the income tax portal" },
    { key: "dsc" as VerificationMode, icon: "🔏", title: "Digital Signature (DSC)", desc: "Use your Class 2 or Class 3 DSC token" },
  ];

  return (
    <div className="tp-modal-backdrop" onClick={onClose}>
      <div className="tp-modal" onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <div className="tp-modal-title-row">
            <div className="tp-modal-icon">🔐</div>
            <div>
              <h3>{title || "Verify your return"}</h3>
              <p>{subtitle || "Your ITR must be verified within 30 days of filing."}</p>
            </div>
          </div>
          <button className="tp-modal-close" onClick={onClose} type="button">✕</button>
        </div>

        {step === "select" && (
          <div className="tp-modal-body">
            <p className="tp-modal-subtitle">Choose how you want to verify:</p>
            <div className="tp-verify-options">
              {MODES.map(m => (
                <button key={m.key as string} className={`tp-verify-option ${mode === m.key ? "selected" : ""}`} onClick={() => setMode(m.key)} type="button">
                  <span className="tp-verify-icon">{m.icon}</span>
                  <div>
                    <strong>{m.title}</strong>
                    {m.recommended && <span className="tp-recommended">Recommended</span>}
                    <p>{m.desc}</p>
                  </div>
                  <div className={`tp-radio ${mode === m.key ? "on" : ""}`} />
                </button>
              ))}
            </div>
            <button className="tp-btn-primary tp-full-width" onClick={() => mode && setStep("verify")} disabled={!mode} type="button">
              Continue →
            </button>
          </div>
        )}

        {step === "verify" && (
          <div className="tp-modal-body">
            {mode === "otp" && (
              <>
                <div className="tp-verify-info">
                  <span className="tp-verify-info-icon">📱</span>
                  <div>
                    <strong>OTP sent to XXXXXXX567</strong>
                    <p>Enter the 6-digit OTP sent to your Aadhaar-linked mobile number.</p>
                  </div>
                </div>
                <div className="tp-otp-boxes">
                  {[0,1,2,3,4,5].map(i => (
                    <input key={i} className="tp-otp-box" maxLength={1} value={otp[i]||""} onChange={e => { const arr = otp.split(""); arr[i]=e.target.value; setOtp(arr.join("")); }} type="text" />
                  ))}
                </div>
                <p className="tp-resend">Didn't receive OTP? <button className="tp-link-btn" type="button">Resend OTP</button></p>
              </>
            )}
            {mode === "evc" && (
              <>
                <div className="tp-verify-info">
                  <span className="tp-verify-info-icon">🏦</span>
                  <div>
                    <strong>Generate EVC from pre-validated bank account</strong>
                    <p>We'll redirect to the Income Tax portal to generate an EVC.</p>
                  </div>
                </div>
                <div className="tp-form-field">
                  <label>Bank account (pre-validated)</label>
                  <input value={bankAcc} onChange={e => setBankAcc(e.target.value)} placeholder="HDFC Bank XXXXXXXXX3456" className="tp-input" />
                </div>
                <div className="tp-form-field">
                  <label>Enter EVC received</label>
                  <input value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter 10-digit EVC" className="tp-input" maxLength={10} />
                </div>
              </>
            )}
            {mode === "net-banking" && (
              <>
                <div className="tp-verify-info">
                  <span className="tp-verify-info-icon">🔐</span>
                  <div>
                    <strong>Redirecting to Income Tax portal</strong>
                    <p>You'll be taken to the ITD portal to login via Net Banking and verify your return.</p>
                  </div>
                </div>
                <div className="tp-netbanking-banks">
                  {["HDFC Bank","SBI","ICICI Bank","Axis Bank","Kotak","Yes Bank"].map(b => (
                    <button key={b} className="tp-bank-chip" onClick={() => setOtp("netbanking")} type="button">{b}</button>
                  ))}
                </div>
              </>
            )}
            {mode === "dsc" && (
              <div className="tp-verify-info">
                <span className="tp-verify-info-icon">🔏</span>
                <div>
                  <strong>Insert your DSC token</strong>
                  <p>Connect your USB token and click Sign. The ITD portal will use your Class 2/3 DSC to sign the XML.</p>
                </div>
              </div>
            )}
            <div className="tp-modal-actions">
              <button className="tp-btn-secondary" onClick={() => setStep("select")} type="button">← Back</button>
              <button className="tp-btn-primary" onClick={verify} disabled={loading || (!otp && mode !== "dsc")} type="button">
                {loading ? "Verifying…" : mode === "net-banking" ? "Go to ITD portal →" : mode === "dsc" ? "Sign with DSC" : "Verify now"}
              </button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="tp-modal-body tp-verify-success">
            <div className="tp-success-orb">✓</div>
            <h3>{successHeadline || "Return verified successfully!"}</h3>
            <p>{successBody || "Your ITR-1 for AY 2026-27 has been verified and submitted to the Income Tax Department."}</p>
            <p className="tp-ack-label">Acknowledgement number: <strong>{ackPrefix || "ITRBNG2026"}-{Math.floor(Math.random()*9000000+1000000)}</strong></p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ITR Filing wizard ───────────────────────────────────────────────────────
const ITR_STEPS: Array<{ key: ItrStep; label: string; icon: string }> = [
  { key: "personal", label: "Personal Info", icon: "👤" },
  { key: "income", label: "Income Sources", icon: "💼" },
  { key: "deductions", label: "Tax Saving", icon: "🛡" },
  { key: "summary", label: "Tax Summary", icon: "📊" },
];

function ItrWizard({ onBack, client, ay }: { onBack: () => void; client: TaxClient; ay: string }) {
  const [step, setStep] = useState<ItrStep>("personal");
  const [personal, setPersonal] = useState<PersonalInfo>({ ...SEED_PERSONAL, name: client.name, pan: client.pan, mobile: client.mobile ?? SEED_PERSONAL.mobile, city: client.city, state: client.state });
  const [income, setIncome] = useState<IncomeData>(SEED_INCOME);
  const [deductions, setDeductions] = useState<DeductionData>(SEED_DEDUCTIONS);
  const [regime, setRegime] = useState<RegimeType>("new");
  const [udin, setUdin] = useState("");
  const [showVerify, setShowVerify] = useState(false);
  const [filed, setFiled] = useState(false);

  const stepIdx = ITR_STEPS.findIndex(s => s.key === step);
  const taxOld = computeTax(income, deductions, "old");
  const taxNew = computeTax(income, deductions, "new");
  const tax = regime === "new" ? taxNew : taxOld;

  const next = () => {
    const idx = ITR_STEPS.findIndex(s => s.key === step);
    if (idx < ITR_STEPS.length - 1) setStep(ITR_STEPS[idx + 1].key);
  };
  const prev = () => {
    const idx = ITR_STEPS.findIndex(s => s.key === step);
    if (idx > 0) setStep(ITR_STEPS[idx - 1].key);
  };

  if (filed) return (
    <div className="tp-filed-screen">
      <div className="tp-filed-orb">✓</div>
      <h2>ITR Filed Successfully!</h2>
      <p>{client.name}'s Income Tax Return for AY {ay} has been filed and verified.</p>
      <div className="tp-filed-details">
        <div><span>Client</span><strong>{client.name}</strong></div>
        <div><span>Return type</span><strong>ITR-1 (Sahaj)</strong></div>
        <div><span>Assessment year</span><strong>AY {ay}</strong></div>
        <div><span>Filing date</span><strong>{new Date().toLocaleDateString("en-IN")}</strong></div>
        <div><span>Status</span><strong className="tp-success-text">Successfully e-verified</strong></div>
        {tax.refundOrDue > 0 ? <div><span>Refund expected</span><strong className="tp-success-text">{inr(tax.refundOrDue)}</strong></div> : null}
      </div>
      <button className="tp-btn-primary" onClick={onBack} type="button">← Back to client list</button>
    </div>
  );

  return (
    <div className="tp-wizard">
      {/* Client / period identity bar */}
      <div className="gst-summary-status-row" style={{ marginBottom: 16 }}>
        <button className="tp-back-link" onClick={onBack} type="button">← Client List</button>
        <strong style={{ marginLeft: 10 }}>{client.name}</strong>
        <span className="tp-muted">· PAN {client.pan}</span>
        <span className="gst-period-chip" style={{ marginLeft: "auto" }}>AY {ay}</span>
      </div>

      {/* Step progress */}
      <div className="tp-step-bar">
        {ITR_STEPS.map((s, i) => (
          <div key={s.key} className="tp-step-item" onClick={() => i <= stepIdx && setStep(s.key)}>
            <div className={`tp-step-dot ${i < stepIdx ? "done" : i === stepIdx ? "active" : ""}`}>
              {i < stepIdx ? "✓" : i === stepIdx ? <span className="tp-orange-dot"/> : i + 1}
            </div>
            <span className={`tp-step-label ${i === stepIdx ? "active" : ""}`}>{s.label}</span>
            {i < ITR_STEPS.length - 1 && <div className={`tp-step-line ${i < stepIdx ? "done" : ""}`} />}
          </div>
        ))}
      </div>

      <div className="tp-wizard-layout">
        {/* Main content */}
        <div className="tp-wizard-main">
          {/* Pre-fill banner */}
          {step === "income" && (
            <div className="tp-prefill-banner">
              <span>↓</span>
              <div>
                <strong>Auto-fill from AIS & Form 26AS</strong>
                <p>Import your TDS credits, advance tax and income details directly from the Income Tax Department.</p>
              </div>
              <button className="tp-btn-outline" type="button">Import from ITD →</button>
            </div>
          )}

          {step === "personal" && (
            <div className="tp-form-section">
              <h3 className="tp-form-section-title">Personal Information</h3>
              <div className="tp-form-grid-2">
                {[
                  ["Full name (as per PAN)", "name", "text", "Raghavendra K."],
                  ["PAN", "pan", "text", "AABCR1234M"],
                  ["Date of birth", "dob", "date", ""],
                  ["Aadhaar number", "aadhaar", "text", "XXXX XXXX 4567"],
                  ["Mobile number", "mobile", "text", "9901234567"],
                  ["Email address", "email", "email", "rk@vaarahi.in"],
                ].map(([label, key, type, ph]) => (
                  <div key={key as string} className="tp-form-field">
                    <label>{label as string}</label>
                    <input className="tp-input" type={type as string} value={(personal as any)[key as string]} onChange={e => setPersonal({ ...personal, [key as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
              </div>
              <h3 className="tp-form-section-title" style={{ marginTop: 24 }}>Address</h3>
              <div className="tp-form-grid-2">
                {[
                  ["Address", "address", "#12, 3rd Cross, Whitefield"],
                  ["City", "city", "Bangalore"],
                  ["State", "state", "Karnataka"],
                  ["Pincode", "pincode", "560066"],
                ].map(([label, key, ph]) => (
                  <div key={key as string} className="tp-form-field">
                    <label>{label as string}</label>
                    <input className="tp-input" value={(personal as any)[key as string]} onChange={e => setPersonal({ ...personal, [key as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
              </div>
              <h3 className="tp-form-section-title" style={{ marginTop: 24 }}>Bank account for refund</h3>
              <div className="tp-form-grid-2">
                {[
                  ["Bank name", "bankName", "HDFC Bank"],
                  ["IFSC code", "ifsc", "HDFC0001234"],
                  ["Account number", "bankAccount", "XXXXXXXXX3456"],
                ].map(([label, key, ph]) => (
                  <div key={key as string} className="tp-form-field">
                    <label>{label as string}</label>
                    <input className="tp-input" value={(personal as any)[key as string]} onChange={e => setPersonal({ ...personal, [key as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
                <div className="tp-form-field">
                  <label>Residential status</label>
                  <select className="tp-input" value={personal.residentialStatus} onChange={e => setPersonal({ ...personal, residentialStatus: e.target.value as any })}>
                    <option value="resident">Resident</option>
                    <option value="nri">NRI</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === "income" && (
            <div className="tp-form-section">
              <h3 className="tp-form-section-title">Income Sources</h3>
              <div className="tp-income-cards">
                {[
                  { icon: "💼", title: "Salary Income", key: "salary", desc: "Add salary income from Form 16. Multiple employers supported.", extra: income.form16Uploaded ? <span className="tp-badge-green">Form 16 uploaded</span> : <button className="tp-btn-outline sm" type="button">Upload Form 16</button> },
                  { icon: "🏦", title: "Interest Income", key: "interestSavings", desc: "Savings bank interest, FD interest, RD interest." },
                  { icon: "📈", title: "Capital Gains", key: "capitalGainsStcg", desc: "Stocks, mutual funds, property, bonds — STCG and LTCG." },
                  { icon: "🏠", title: "House Property", key: "housePropertyRent", desc: "Rental income or interest on home loan." },
                  { icon: "₿", title: "Crypto & VDA Income", key: "cryptoIncome", desc: "Crypto, NFT, and virtual digital asset gains at flat 30%." },
                  { icon: "💡", title: "Other Income", key: "otherIncome", desc: "Online gaming, gifts, dividend, freelancing, etc." },
                ].map(item => (
                  <div key={item.key} className="tp-income-card">
                    <div className="tp-income-card-left">
                      <span className="tp-income-icon">{item.icon}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.desc}</p>
                        {item.extra && <div style={{ marginTop: 6 }}>{item.extra}</div>}
                      </div>
                    </div>
                    <div className="tp-income-card-right">
                      <div className="tp-form-field" style={{ marginBottom: 0 }}>
                        <label>Amount (₹)</label>
                        <input className="tp-input sm" type="number" value={(income as any)[item.key] || ""} onChange={e => setIncome({ ...income, [item.key]: Number(e.target.value) })} placeholder="0" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === "deductions" && (
            <div className="tp-form-section">
              <div className="tp-prefill-banner" style={{ marginBottom: 16 }}>
                <span>↓</span>
                <div>
                  <strong>Import Form 26AS & AIS</strong>
                  <p>Auto-fill TDS credits and tax paid details from the Income Tax portal.</p>
                </div>
                <button className="tp-btn-outline" type="button">Import from ITD →</button>
              </div>
              <h3 className="tp-form-section-title">Tax Saving Deductions (Chapter VI-A)</h3>
              <div className="tp-deduction-cards">
                {[
                  { key: "sec80C", label: "Section 80C", max: 150000, desc: "PPF, ELSS, LIC premium, EPF, NSC, home loan principal" },
                  { key: "sec80D", label: "Section 80D", max: 75000, desc: "Health insurance premium — self, family and parents" },
                  { key: "sec80CCD1B", label: "Section 80CCD(1B)", max: 50000, desc: "NPS contribution (additional ₹50,000 over 80C limit)" },
                  { key: "sec80TTA", label: "Section 80TTA / 80TTB", max: 10000, desc: "Savings bank interest deduction" },
                  { key: "sec80G", label: "Section 80G", max: 0, desc: "Donations to approved funds and institutions" },
                  { key: "sec80E", label: "Section 80E", max: 0, desc: "Interest on education loan — no maximum limit" },
                  { key: "sec80EEA", label: "Section 80EEA", max: 150000, desc: "Interest on home loan for affordable housing" },
                ].map(item => {
                  const val = (deductions as any)[item.key] as number;
                  const pct = item.max > 0 ? Math.min(100, Math.round(val / item.max * 100)) : 0;
                  return (
                    <div key={item.key} className="tp-deduction-card">
                      <div className="tp-deduction-header">
                        <div>
                          <strong>{item.label}</strong>
                          {item.max > 0 && <span className="tp-max-limit">Max {inr(item.max)}</span>}
                          <p>{item.desc}</p>
                        </div>
                        <input className="tp-input sm" type="number" value={val || ""} onChange={e => setDeductions({ ...deductions, [item.key]: Number(e.target.value) })} placeholder="0" />
                      </div>
                      {item.max > 0 && val > 0 && (
                        <div className="tp-deduction-bar">
                          <div className="tp-deduction-fill" style={{ width: `${pct}%`, background: pct >= 100 ? "#10b981" : "#2563eb" }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <h3 className="tp-form-section-title" style={{ marginTop: 20 }}>Taxes Paid</h3>
              <div className="tp-form-grid-2">
                {[
                  ["TDS credited (Form 26AS)", "tdsCredited"],
                  ["TCS credits", "tcsCredits"],
                  ["Advance tax paid", "advanceTax"],
                  ["Self assessment tax", "selfAssessmentTax"],
                ].map(([label, key]) => (
                  <div key={key} className="tp-form-field">
                    <label>{label}</label>
                    <input className="tp-input" type="number" value={(deductions as any)[key] || ""} onChange={e => setDeductions({ ...deductions, [key]: Number(e.target.value) })} placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === "summary" && (
            <div className="tp-summary-section">
              {/* Regime comparison */}
              <div className="tp-regime-compare">
                <h3 className="tp-form-section-title">Choose your tax regime</h3>
                <div className="tp-regime-cards">
                  {(["new", "old"] as RegimeType[]).map(r => {
                    const t = r === "new" ? taxNew : taxOld;
                    const isSelected = regime === r;
                    const isBetter = r === "new" ? taxNew.totalTax <= taxOld.totalTax : taxOld.totalTax < taxNew.totalTax;
                    return (
                      <button key={r} className={`tp-regime-card ${isSelected ? "selected" : ""}`} onClick={() => setRegime(r)} type="button">
                        <div className="tp-regime-card-header">
                          <strong>{r === "new" ? "New Regime" : "Old Regime"}</strong>
                          {isBetter && <span className="tp-regime-badge">Better for you</span>}
                          {isSelected && <span className="tp-regime-selected-mark">✓ Selected</span>}
                        </div>
                        <div className="tp-regime-numbers">
                          <div><span>Gross Income</span><strong>{inr(t.gross)}</strong></div>
                          <div><span>Deductions</span><strong>{inr(t.totalDeductions)}</strong></div>
                          <div><span>Taxable Income</span><strong>{inr(t.taxableIncome)}</strong></div>
                          <div className="tp-regime-tax-row"><span>Total Tax</span><strong className={t.totalTax === 0 ? "tp-success-text" : ""}>{inr(t.totalTax)}</strong></div>
                        </div>
                        {t.refundOrDue >= 0 ? (
                          <div className="tp-regime-refund">Refund: <strong className="tp-success-text">{inr(t.refundOrDue)}</strong></div>
                        ) : (
                          <div className="tp-regime-due">Tax due: <strong className="tp-error-text">{inr(Math.abs(t.refundOrDue))}</strong></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Detailed summary */}
              <div className="tp-tax-summary-card">
                <h3>Detailed Tax Computation</h3>
                <div className="tp-summary-rows">
                  {[
                    ["Gross Income", tax.gross, false],
                    ["Less: Deductions & Standard Deduction", tax.totalDeductions, false],
                    ["Taxable Income", tax.taxableIncome, true],
                    ["Income Tax", tax.tax, false],
                    ["Health & Education Cess (4%)", tax.cess, false],
                    ["Total Tax Liability", tax.totalTax, true],
                    ["Less: TDS / TCS / Advance Tax Paid", tax.taxPaid, false],
                  ].map(([label, val, bold]) => (
                    <div key={label as string} className={`tp-summary-row ${bold ? "bold" : ""}`}>
                      <span>{label}</span>
                      <strong>{inr(val as number)}</strong>
                    </div>
                  ))}
                  <div className={`tp-summary-row tp-summary-final ${tax.refundOrDue >= 0 ? "refund" : "due"}`}>
                    <span>{tax.refundOrDue >= 0 ? "Refund Receivable" : "Tax Payable"}</span>
                    <strong>{inr(Math.abs(tax.refundOrDue))}</strong>
                  </div>
                </div>
              </div>

              <button className="tp-btn-primary tp-cta-file" onClick={() => setShowVerify(true)} type="button">
                File ITR & Verify →
              </button>
            </div>
          )}

          <div className="tp-wizard-nav">
            {stepIdx > 0 && <button className="tp-btn-secondary" onClick={prev} type="button">← Previous</button>}
            {stepIdx < ITR_STEPS.length - 1 && <button className="tp-btn-primary" onClick={next} type="button">Save & Continue →</button>}
          </div>
        </div>

        {/* Right sidebar */}
        <aside className="tp-wizard-sidebar">
          <div className="tp-sidebar-card tp-live-support">
            <div className="tp-support-icon">💬</div>
            <div>
              <strong>Live Support</strong>
              <p>Ask our CA for ITR doubts and queries</p>
              <button className="tp-link-btn" type="button">Chat now →</button>
            </div>
          </div>
          <div className="tp-sidebar-card">
            <div className="tp-ai-insight-header">
              <span className="tp-ai-badge">✦ AI</span>
              <strong>Smart insights</strong>
            </div>
            <div className="tp-insights">
              {regime === "new" && taxNew.totalTax <= taxOld.totalTax ? (
                <div className="tp-insight green">✓ New regime saves you {inr(taxOld.totalTax - taxNew.totalTax)} vs old regime</div>
              ) : (
                <div className="tp-insight blue">✓ Old regime saves you {inr(taxNew.totalTax - taxOld.totalTax)} with your deductions</div>
              )}
              {deductions.sec80C < 150000 && <div className="tp-insight orange">You can claim ₹{(150000 - deductions.sec80C).toLocaleString("en-IN")} more under 80C to reduce tax</div>}
              {income.interestSavings > 0 && deductions.sec80TTA === 0 && <div className="tp-insight blue">Add ₹{Math.min(10000, income.interestSavings).toLocaleString("en-IN")} savings interest as 80TTA deduction</div>}
            </div>
          </div>
          <div className="tp-sidebar-card tp-expert-card">
            <strong>Need an expert to file?</strong>
            <p>Let a CA handle it — you relax.</p>
            <button className="tp-btn-outline" type="button">Book a CA →</button>
          </div>
          <div className="tp-sidebar-card tp-itr-type-card">
            <div><span>ITR Type</span><strong className="tp-badge-blue">ITR-1</strong></div>
            <div style={{ marginTop: 8 }}><span>Assessment Year</span><strong>AY {ay}</strong></div>
            <div style={{ marginTop: 8 }}><span>Regime</span><strong>{regime === "new" ? "New Regime" : "Old Regime"}</strong></div>
            <div style={{ marginTop: 8 }}>
              <span>UDIN {udin ? "" : "(if CA-certified)"}</span>
              {udin ? <strong>{udin}</strong> : (
                <input className="tp-tds-input" style={{ marginTop: 4, width: "100%" }} placeholder="18-digit UDIN" maxLength={18}
                  onBlur={e => e.target.value.length === 18 && setUdin(e.target.value)} />
              )}
            </div>
          </div>
        </aside>
      </div>

      {showVerify && (
        <VerificationModal
          onClose={() => setShowVerify(false)}
          onSuccess={() => { setShowVerify(false); setFiled(true); }}
          subtitle={`${client.name}'s ITR must be verified within 30 days of filing.`}
          successBody={`${client.name}'s ITR for AY ${ay} has been verified and submitted to the Income Tax Department.`}
        />
      )}
    </div>
  );
}

// ─── TDS Return module ───────────────────────────────────────────────────────
function TdsReturn({ onBack, client, fy: initialFy }: { onBack: () => void; client: TaxClient; fy: string }) {
  const [form, setForm] = useState<"26Q" | "24Q" | "27EQ">("26Q");
  const [quarter, setQuarter] = useState("Q1");
  const [fy, setFy] = useState(initialFy);
  const [activeTab, setActiveTab] = useState<TdsTab>("deductor");
  const [deductor, setDeductor] = useState<TdsDeductor>({ tan: client.tan ?? "", pan: client.pan, name: client.name, type: "Company", address: "", city: client.city, state: client.state, pincode: "", email: "", phone: client.mobile ?? "", responsibleName: client.contactPerson, responsibleDesignation: "" });
  const [deductees, setDeductees] = useState<TdsDeductee[]>([]);
  const [challans, setChallans] = useState<TdsChallan[]>([]);
  const [deductions, setDeductions] = useState<TdsDeduction[]>([]);

  const TDS_TABS: Array<{ key: TdsTab; label: string }> = [
    { key: "deductor", label: "Deductor" },
    { key: "deductee", label: "Deductee" },
    { key: "challan", label: "Challan" },
    { key: "deduction", label: "Deduction" },
  ];

  const addDeductee = () => setDeductees(prev => [...prev, { id: uid(), name: "", pan: "", type: "Company", panStatus: "Valid" }]);
  const addChallan = () => setChallans(prev => [...prev, { id: uid(), date: "", section: "194C", tax: 0, interest: 0, fee: 0, other: 0, total: 0, bsrCode: "", serialNo: "", verificationResult: "Pending" }]);
  const addDeduction = () => setDeductions(prev => [...prev, { id: uid(), deducteeName: "", deducteePAN: "", amountPaid: 0, paidDate: "", deductionDate: "", section: "194C", taxRate: 2, taxDeducted: 0, challanId: "", challanManual: false }]);

  // Auto-challan matching: for a given section & tax amount, find the challan on the same
  // section with enough unutilised balance (excluding this deduction's own current contribution).
  const matchChallan = (section: string, neededTax: number, selfId: string): string => {
    if (neededTax <= 0) return "";
    const utilised = new Map<string, number>();
    for (const d of deductions) {
      if (d.id === selfId || !d.challanId) continue;
      utilised.set(d.challanId, (utilised.get(d.challanId) || 0) + d.taxDeducted);
    }
    const candidates = challans
      .filter(c => c.section === section)
      .map(c => ({ id: c.id, remaining: c.total - (utilised.get(c.id) || 0) }))
      .filter(c => c.remaining >= neededTax)
      .sort((a, b) => a.remaining - b.remaining); // tightest fit first
    return candidates[0]?.id || "";
  };

  const totalDeductedTax = deductions.reduce((sum, d) => sum + d.taxDeducted, 0);
  const totalChallanAmount = challans.reduce((sum, c) => sum + c.total, 0);
  const balance = totalChallanAmount - totalDeductedTax;

  return (
    <div className="tp-tds-shell">
      {/* TDS header bar (Winman-style top bar but web-native) */}
      <div className="tp-tds-topbar">
        <div className="tp-tds-topbar-left">
          <button className="tp-back-link" onClick={onBack} type="button">← Client List</button>
          <div className="tp-tds-meta"><strong>{client.name}</strong><span className="tp-muted">· TAN {client.tan}</span></div>
          <div className="tp-tds-form-selector">
            <span>Form :</span>
            <div className="tp-tds-form-tabs">
              {(["26Q", "24Q", "27EQ"] as const).map(f => <button key={f} className={`tp-tds-form-tab ${form === f ? "active" : ""}`} onClick={() => setForm(f)} type="button">{f}</button>)}
            </div>
          </div>
          <div className="tp-tds-meta">
            <span>Quarter :</span>
            <select className="tp-tds-select" value={quarter} onChange={e => setQuarter(e.target.value)}>
              {["Q1", "Q2", "Q3", "Q4"].map(q => <option key={q}>{q}</option>)}
            </select>
            <span>Fin Year :</span>
            <select className="tp-tds-select" value={fy} onChange={e => setFy(e.target.value)}>
              {["2024-25", "2025-26", "2026-27"].map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="tp-tds-topbar-right">
          <div className="tp-tds-stats">
            <div className="tp-tds-stat"><span>Challan Total</span><strong>{inr(totalChallanAmount)}</strong></div>
            <div className="tp-tds-stat"><span>Tax Deducted</span><strong>{inr(totalDeductedTax)}</strong></div>
            <div className={`tp-tds-stat ${balance > 0 ? "green" : balance < 0 ? "red" : ""}`}><span>Balance</span><strong>{inr(Math.abs(balance))}</strong></div>
          </div>
          <button className="tp-btn-primary sm" type="button">Validate & Generate FVU</button>
          <button className="tp-btn-secondary sm" type="button">Export to Text</button>
        </div>
      </div>

      {/* Section title */}
      <div className="tp-tds-section-header">
        <h2 className="tp-tds-section-title">
          {activeTab === "deductor" ? "Deductor Details" : activeTab === "deductee" ? "Deductees" : activeTab === "challan" ? "Challans" : "Deductions"}
        </h2>
        <span className="tp-tds-form-badge">Form {form} · {quarter} · FY {fy}</span>
      </div>

      {/* Content */}
      <div className="tp-tds-content">
        {activeTab === "deductor" && (
          <div className="tp-tds-form-grid">
            <div className="tp-tds-panel">
              <div className="tp-tds-panel-title">Deductor's Details</div>
              <div className="tp-tds-field-grid">
                {[["TAN *", "tan", "BLRA12345B"], ["PAN *", "pan", "AABCV1234M"], ["GSTIN", "gstin" as any, ""], ["Name *", "name", "Vaarahi RMC Pvt Ltd"], ["Name as per dept. records *", "nameAsDept" as any, ""], ["Type *", "type", ""]].map(([label, key, ph]) => (
                  key === "type" ? (
                    <div key={key as string} className="tp-tds-field">
                      <label>{label}</label>
                      <select className="tp-tds-input" value={deductor.type} onChange={e => setDeductor({ ...deductor, type: e.target.value })}>
                        {["Company", "Firm", "Individual", "HUF", "AOP/BOI", "Trust", "AJP"].map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div key={key as string} className="tp-tds-field">
                      <label>{label}</label>
                      <input className="tp-tds-input" value={(deductor as any)[key as string] || ""} onChange={e => setDeductor({ ...deductor, [key as string]: e.target.value })} placeholder={ph as string} />
                    </div>
                  )
                ))}
                {[["Door No", ""], ["Building", ""], ["Road", ""], ["Area", ""], ["City *", "Bangalore"], ["State *", "Karnataka"], ["Pincode *", "560066"]].map(([label, ph]) => (
                  <div key={label} className="tp-tds-field">
                    <label>{label}</label>
                    <input className="tp-tds-input" placeholder={ph} />
                  </div>
                ))}
                {[["STD Code", ""], ["Telephone *", "9901234567"], ["E-Mail *", "rk@vaarahi.in"], ["Branch/Division", ""]].map(([label, ph]) => (
                  <div key={label} className="tp-tds-field">
                    <label>{label}</label>
                    <input className="tp-tds-input" placeholder={ph} />
                  </div>
                ))}
              </div>
            </div>
            <div className="tp-tds-panel">
              <div className="tp-tds-panel-title">Responsible Person's Details</div>
              <div className="tp-tds-field-grid">
                {[["Name *", "responsibleName", "Raghavendra K."], ["Designation *", "responsibleDesignation", "Director"], ["Door No *", "", ""], ["Building", "", ""], ["Road", "", ""], ["Area", "", ""], ["City *", "", "Bangalore"], ["State *", "", "Karnataka"], ["Pincode *", "", "560066"], ["STD Code", "", ""], ["Telephone *", "", "9901234567"], ["E-Mail *", "", "rk@vaarahi.in"], ["PAN *", "", "AABCR1234M"], ["Father's Name", "", ""], ["Mobile No. *", "", "9901234567"], ["Gender", "", ""]].map(([label, key, ph]) => (
                  <div key={label as string} className="tp-tds-field">
                    <label>{label}</label>
                    <input className="tp-tds-input" value={key ? (deductor as any)[key as string] || "" : ""} onChange={key ? e => setDeductor({ ...deductor, [key as string]: e.target.value }) : undefined} placeholder={ph as string} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "deductee" && (
          <div className="tp-tds-table-section">
            <div className="tp-tds-table-header">
              <span>{deductees.length} deductee{deductees.length !== 1 ? "s" : ""}</span>
              <button className="tp-btn-primary sm" onClick={addDeductee} type="button">+ Add Deductee</button>
            </div>
            <div className="tp-tds-spreadsheet">
              <div className="tp-tds-ss-header">
                <div className="tp-tds-ss-id">ID No.</div>
                <div className="tp-tds-ss-col flex2">Name *</div>
                <div className="tp-tds-ss-col flex2">PAN ? *</div>
                <div className="tp-tds-ss-col">Type of Deductee *</div>
                <div className="tp-tds-ss-col">PAN Validation</div>
                <div className="tp-tds-ss-col">PAN Holder's Name ?</div>
                <div className="tp-tds-ss-action" />
              </div>
              {deductees.length === 0 ? (
                <div className="tp-tds-empty">No deductees added. Click "+ Add Deductee" to begin.</div>
              ) : deductees.map((d, i) => (
                <div key={d.id} className="tp-tds-ss-row">
                  <div className="tp-tds-ss-id">{i + 1}</div>
                  <div className="tp-tds-ss-col flex2"><input className="tp-tds-cell-input" value={d.name} onChange={e => setDeductees(prev => prev.map(x => x.id === d.id ? { ...x, name: e.target.value } : x))} placeholder="Full name" /></div>
                  <div className="tp-tds-ss-col flex2"><input className="tp-tds-cell-input" value={d.pan} onChange={e => setDeductees(prev => prev.map(x => x.id === d.id ? { ...x, pan: e.target.value.toUpperCase() } : x))} maxLength={10} placeholder="ABCDE1234F" /></div>
                  <div className="tp-tds-ss-col">
                    <select className="tp-tds-cell-select" value={d.type} onChange={e => setDeductees(prev => prev.map(x => x.id === d.id ? { ...x, type: e.target.value } : x))}>
                      {["Company", "Individual", "HUF", "Firm", "AOP/BOI"].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  {(() => {
                    const panValid = PAN_REGEX.test(d.pan);
                    const status = d.pan.length === 0 ? "Pending" : panValid ? "Valid" : "Invalid format";
                    return <div className="tp-tds-ss-col"><span className={`tp-pan-status ${panValid ? "valid" : d.pan.length > 0 ? "invalid" : ""}`}>{status}</span></div>;
                  })()}
                  <div className="tp-tds-ss-col tp-muted">{PAN_REGEX.test(d.pan) ? d.name : "—"}</div>
                  <div className="tp-tds-ss-action"><button className="tp-tds-del-btn" onClick={() => setDeductees(prev => prev.filter(x => x.id !== d.id))} type="button">✕</button></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "challan" && (
          <div className="tp-tds-table-section">
            <div className="tp-tds-table-header">
              <span>{challans.length} challan{challans.length !== 1 ? "s" : ""} · Total: {inr(totalChallanAmount)}</span>
              <button className="tp-btn-primary sm" onClick={addChallan} type="button">+ Add Challan</button>
            </div>
            <div className="tp-tds-spreadsheet">
              <div className="tp-tds-ss-header">
                <div className="tp-tds-ss-id">ID</div>
                <div className="tp-tds-ss-col">Date of Challan *</div>
                <div className="tp-tds-ss-col">Section</div>
                <div className="tp-tds-ss-col">Tax Deposited</div>
                <div className="tp-tds-ss-col">Interest</div>
                <div className="tp-tds-ss-col">Fee</div>
                <div className="tp-tds-ss-col">Total Amount</div>
                <div className="tp-tds-ss-col flex2">BSR Code / Serial No. *</div>
                <div className="tp-tds-ss-col">Verification</div>
                <div className="tp-tds-ss-action" />
              </div>
              {challans.length === 0 ? (
                <div className="tp-tds-empty">No challans added. Click "+ Add Challan" to begin.</div>
              ) : challans.map((c, i) => (
                <div key={c.id} className="tp-tds-ss-row">
                  <div className="tp-tds-ss-id">{i + 1}</div>
                  <div className="tp-tds-ss-col"><input type="date" className="tp-tds-cell-input" value={c.date} onChange={e => setChallans(prev => prev.map(x => x.id === c.id ? { ...x, date: e.target.value } : x))} /></div>
                  <div className="tp-tds-ss-col">
                    <select className="tp-tds-cell-select" value={c.section} onChange={e => setChallans(prev => prev.map(x => x.id === c.id ? { ...x, section: e.target.value } : x))}>
                      {["194C","194J","194A","194H","194I","194Q","192"].map(s => <option key={s}>{s}</option>)}
                    </select>
                    <div className="tp-muted" style={{ fontSize: 11, marginTop: 2 }}>{SECTION_393_MAP[c.section] || ""}</div>
                  </div>
                  {(["tax","interest","fee","other"] as const).map(field => (
                    <div key={field} className="tp-tds-ss-col">
                      <input type="number" className="tp-tds-cell-input" value={(c as any)[field] || ""} onChange={e => {
                        const updated = { ...c, [field]: Number(e.target.value) };
                        updated.total = updated.tax + updated.interest + updated.fee + updated.other;
                        setChallans(prev => prev.map(x => x.id === c.id ? updated : x));
                      }} placeholder="0" />
                    </div>
                  ))}
                  <div className="tp-tds-ss-col"><strong>{inr(c.total)}</strong></div>
                  <div className="tp-tds-ss-col flex2" style={{ display: "flex", gap: 4 }}>
                    <input className="tp-tds-cell-input" value={c.bsrCode} onChange={e => setChallans(prev => prev.map(x => x.id === c.id ? { ...x, bsrCode: e.target.value } : x))} placeholder="BSR Code" style={{ width: "50%" }} />
                    <input className="tp-tds-cell-input" value={c.serialNo} onChange={e => setChallans(prev => prev.map(x => x.id === c.id ? { ...x, serialNo: e.target.value } : x))} placeholder="Serial No." style={{ width: "50%" }} />
                  </div>
                  <div className="tp-tds-ss-col"><span className={`tp-pan-status ${c.bsrCode && c.serialNo ? "valid" : ""}`}>{c.bsrCode && c.serialNo ? "Verified" : "Pending"}</span></div>
                  <div className="tp-tds-ss-action"><button className="tp-tds-del-btn" onClick={() => setChallans(prev => prev.filter(x => x.id !== c.id))} type="button">✕</button></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "deduction" && (
          <div className="tp-tds-table-section">
            <div className="tp-tds-table-header">
              <div className="tp-tds-balance-bar">
                <span>Challan Amount: <strong>{inr(totalChallanAmount)}</strong></span>
                <span>Amount Utilised: <strong>{inr(totalDeductedTax)}</strong></span>
                <span className={balance >= 0 ? "tp-success-text" : "tp-error-text"}>Balance: <strong>{inr(balance)}</strong></span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <select className="tp-tds-select">
                  <option value="">Select Challan</option>
                  {challans.map((c, i) => <option key={c.id} value={c.id}>Challan {i + 1} — {inr(c.total)}</option>)}
                </select>
                <button className="tp-btn-primary sm" onClick={addDeduction} type="button">+ Add Deduction</button>
              </div>
            </div>
            <div className="tp-tds-spreadsheet">
              <div className="tp-tds-ss-header">
                <div className="tp-tds-ss-id">ID</div>
                <div className="tp-tds-ss-col flex2">Name (Deductee)</div>
                <div className="tp-tds-ss-col">PAN</div>
                <div className="tp-tds-ss-col">Amount Paid *</div>
                <div className="tp-tds-ss-col">Paid / Credited Date *</div>
                <div className="tp-tds-ss-col">Deduction Date</div>
                <div className="tp-tds-ss-col">Section *</div>
                <div className="tp-tds-ss-col">Rate %</div>
                <div className="tp-tds-ss-col">Tax Deducted</div>
                <div className="tp-tds-ss-col flex2">Matched Challan</div>
                <div className="tp-tds-ss-action" />
              </div>
              {deductions.length === 0 ? (
                <div className="tp-tds-empty">No deductions added. Select a challan and click "+ Add Deduction".</div>
              ) : deductions.map((d, i) => (
                <div key={d.id} className="tp-tds-ss-row">
                  <div className="tp-tds-ss-id">{i + 1}</div>
                  <div className="tp-tds-ss-col flex2">
                    <select className="tp-tds-cell-select" value={d.deducteeName} onChange={e => setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, deducteeName: e.target.value, deducteePAN: deductees.find(de => de.name === e.target.value)?.pan || "" } : x))}>
                      <option value="">Select deductee</option>
                      {deductees.map(de => <option key={de.id} value={de.name}>{de.name}</option>)}
                    </select>
                  </div>
                  <div className="tp-tds-ss-col"><input className="tp-tds-cell-input" value={d.deducteePAN} readOnly placeholder="Auto-fill" /></div>
                  <div className="tp-tds-ss-col"><input type="number" className="tp-tds-cell-input" value={d.amountPaid || ""} onChange={e => {
                    const amt = Number(e.target.value);
                    const tax = Math.round(amt * d.taxRate / 100);
                    setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, amountPaid: amt, taxDeducted: tax, challanId: x.challanManual ? x.challanId : matchChallan(x.section, tax, x.id) } : x));
                  }} placeholder="0" /></div>
                  <div className="tp-tds-ss-col"><input type="date" className="tp-tds-cell-input" value={d.paidDate} onChange={e => setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, paidDate: e.target.value, deductionDate: e.target.value } : x))} /></div>
                  <div className="tp-tds-ss-col"><input type="date" className="tp-tds-cell-input" value={d.deductionDate} onChange={e => setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, deductionDate: e.target.value } : x))} /></div>
                  <div className="tp-tds-ss-col">
                    <select className="tp-tds-cell-select" value={d.section} onChange={e => setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, section: e.target.value, challanId: x.challanManual ? x.challanId : matchChallan(e.target.value, x.taxDeducted, x.id) } : x))}>
                      {["194C","194J","194A","194H","194I","194Q","192","194B"].map(s => <option key={s}>{s}</option>)}
                    </select>
                    <div className="tp-muted" style={{ fontSize: 11, marginTop: 2 }}>{SECTION_393_MAP[d.section] || ""}</div>
                  </div>
                  <div className="tp-tds-ss-col"><input type="number" className="tp-tds-cell-input" value={d.taxRate} onChange={e => {
                    const rate = Number(e.target.value);
                    const tax = Math.round(d.amountPaid * rate / 100);
                    setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, taxRate: rate, taxDeducted: tax, challanId: x.challanManual ? x.challanId : matchChallan(x.section, tax, x.id) } : x));
                  }} placeholder="2" /></div>
                  <div className="tp-tds-ss-col"><strong style={{ color: "#10b981" }}>{inr(d.taxDeducted)}</strong></div>
                  <div className="tp-tds-ss-col flex2">
                    <select className="tp-tds-cell-select" value={d.challanId} onChange={e => setDeductions(prev => prev.map(x => x.id === d.id ? { ...x, challanId: e.target.value, challanManual: true } : x))}>
                      <option value="">— No match —</option>
                      {challans.map((c, i) => <option key={c.id} value={c.id}>Challan {i + 1} · {c.section} · {inr(c.total)}</option>)}
                    </select>
                    {d.challanId && !d.challanManual && <span className="tp-pan-status valid" style={{ marginTop: 2, display: "inline-block" }}>Auto-matched</span>}
                    {!d.challanId && d.taxDeducted > 0 && <span className="tp-pan-status invalid" style={{ marginTop: 2, display: "inline-block" }}>No challan covers this</span>}
                  </div>
                  <div className="tp-tds-ss-action"><button className="tp-tds-del-btn" onClick={() => setDeductions(prev => prev.filter(x => x.id !== d.id))} type="button">✕</button></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom tab bar — Winman-style */}
      <div className="tp-tds-bottom-bar">
        <div className="tp-tds-bottom-left">
          {activeTab === "deductor" && <span className="tp-tds-due-link">Due dates</span>}
          {activeTab === "challan" && <span className="tp-tds-due-link">How to add challans in revised return?</span>}
          {activeTab === "deduction" && <span className="tp-tds-due-link">Due dates · How to import data from Excel?</span>}
        </div>
        <div className="tp-tds-bottom-tabs">
          {TDS_TABS.map(tab => (
            <button key={tab.key} className={`tp-tds-bottom-tab ${activeTab === tab.key ? "active" : ""}`} onClick={() => setActiveTab(tab.key)} type="button">{tab.label}</button>
          ))}
        </div>
        <div className="tp-tds-bottom-right">
          {(activeTab === "deductee" || activeTab === "challan" || activeTab === "deduction") && (
            <div className="tp-tds-nav-btns">
              <button className="tp-tds-nav-btn" type="button">|&lt;</button>
              <button className="tp-tds-nav-btn" type="button">&gt;|</button>
              <span className="tp-tds-count">
                {activeTab === "deductee" ? `Number of Deductees: ${deductees.length}` : activeTab === "challan" ? `Number of Challans: ${challans.length}` : `Number of Deductions: ${deductions.length}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TaxPilot Dashboard ───────────────────────────────────────────────────────
function TaxPilotDashboard({ onStartItr, onStartTds, onStartGst }: { onStartItr: () => void; onStartTds: () => void; onStartGst: () => void }) {
  return (
    <div className="tp-dashboard">
      <div className="tp-dash-hero">
        <div>
          <div className="tp-dash-greeting">AY 2026-27 · Filing Season Open</div>
          <h2>Welcome back, Raghavendra K.</h2>
          <p className="tp-dash-sub">PAN: AABCR1234M · Filing Status: <span className="tp-badge-orange">In Progress</span></p>
        </div>
        <div className="tp-dash-hero-stats">
          <div className="tp-dash-stat"><strong>3</strong><span>Clients' ITR pending</span></div>
          <div className="tp-dash-stat"><strong>2</strong><span>TDS returns due</span></div>
          <div className="tp-dash-stat tp-stat-green"><strong>₹1.2L</strong><span>Refund expected</span></div>
        </div>
      </div>

      <div className="tp-dash-grid">
        {/* ITR card */}
        <div className="tp-dash-module-card" onClick={onStartItr}>
          <div className="tp-dash-module-icon itr">📄</div>
          <div className="tp-dash-module-content">
            <strong>Income Tax Return (ITR)</strong>
            <p>File ITR-1, ITR-2, ITR-3, ITR-4 with pre-fill from AIS/26AS. Old vs New regime comparison. OTP/EVC/DSC verification.</p>
            <div className="tp-dash-module-tags">
              <span>ITR-1 Sahaj</span><span>ITR-2</span><span>ITR-3</span><span>ITR-4 Sugam</span>
            </div>
          </div>
          <div className="tp-dash-module-status">
            <span className="tp-badge-orange">2 pending</span>
            <span className="tp-arrow">→</span>
          </div>
        </div>

        {/* TDS card */}
        <div className="tp-dash-module-card" onClick={onStartTds}>
          <div className="tp-dash-module-icon tds">🧾</div>
          <div className="tp-dash-module-content">
            <strong>TDS / TCS Return</strong>
            <p>File Form 24Q, 26Q, 27EQ. Web-based Winman-style grid entry. Challan matching, deductee PAN validation, FVU generation.</p>
            <div className="tp-dash-module-tags">
              <span>Form 24Q</span><span>Form 26Q</span><span>Form 27EQ</span><span>2025 Act aware</span>
            </div>
          </div>
          <div className="tp-dash-module-status">
            <span className="tp-badge-orange">Q1 due</span>
            <span className="tp-arrow">→</span>
          </div>
        </div>

        {/* GST card */}
        <div className="tp-dash-module-card" onClick={onStartGst}>
          <div className="tp-dash-module-icon gst">📋</div>
          <div className="tp-dash-module-content">
            <strong>GST Returns</strong>
            <p>GSTR-1, GSTR-3B, GSTR-9 filing with ITC reconciliation and 2B matching.</p>
            <div className="tp-dash-module-tags"><span>GSTR-1</span><span>GSTR-3B</span><span>GSTR-9</span></div>
          </div>
          <div className="tp-dash-module-status"><span className="tp-badge-slate">Spec pending</span><span className="tp-arrow">→</span></div>
        </div>

        {/* Compliance calendar */}
        <div className="tp-dash-compliance-card">
          <strong>Upcoming deadlines</strong>
          <div className="tp-compliance-rows">
            {[
              { label: "GSTR-1 (July)", date: "11-Aug-2026", urgent: true },
              { label: "TDS 26Q Q1", date: "31-Jul-2026", urgent: true },
              { label: "GSTR-3B (July)", date: "20-Aug-2026", urgent: false },
              { label: "ITR Filing", date: "31-Oct-2026", urgent: false },
              { label: "TDS 26Q Q2", date: "31-Oct-2026", urgent: false },
            ].map(item => (
              <div key={item.label} className="tp-compliance-row">
                <div className={`tp-compliance-dot ${item.urgent ? "urgent" : ""}`} />
                <span>{item.label}</span>
                <span className={`tp-compliance-date ${item.urgent ? "urgent" : ""}`}>{item.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TaxPilot shell ──────────────────────────────────────────────────────────
interface TaxPilotProps {
  navigate: (v: string) => void;
  notify: (m: string, t?: string) => void;
  userName?: string;
}

export function TaxPilotView({ navigate, notify, userName = "Raghavendra K." }: TaxPilotProps) {
  const [module, setModule] = useState<TaxModule>("dashboard");

  return (
    <div className="tp-shell">
      {/* Sidebar */}
      <aside className="tp-sidebar">
        <div className="tp-sidebar-brand">
          <img src="/product-icons/taxpilot.png" alt="TaxPilot" className="tp-sidebar-logo-img" />
          <div className="tp-sidebar-wordmark">
            <span className="tp-wm-first">Tax</span><span className="tp-wm-last">Pilot</span>
          </div>
        </div>
        <nav className="tp-sidebar-nav">
          {[
            { key: "dashboard" as TaxModule, icon: "⊞", label: "Dashboard" },
            { key: "itr" as TaxModule, icon: "📄", label: "ITR Filing" },
            { key: "tds" as TaxModule, icon: "🧾", label: "TDS / TCS Return" },
            { key: "gst" as TaxModule, icon: "📊", label: "GST Returns" },
          ].map(item => (
            <button key={item.key} className={`tp-nav-btn ${module === item.key ? "active" : ""}`} onClick={() => setModule(item.key)} type="button">
              <span className="tp-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
          <div className="tp-nav-divider" />
          {[
            { icon: "📑", label: "Statutory Compliance", soon: true },
          ].map(item => (
            <button key={item.label} className="tp-nav-btn disabled" type="button" disabled>
              <span className="tp-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
              <span className="tp-nav-soon">Soon</span>
            </button>
          ))}
        </nav>
        <div className="tp-sidebar-footer">
          <button className="tp-nav-btn interlink" onClick={() => navigate("clients")} type="button">
            <span className="tp-nav-icon">👥</span>
            <span>ClientDesk ↗</span>
          </button>
          <button className="tp-nav-btn interlink" onClick={() => navigate("audit")} type="button">
            <span className="tp-nav-icon">🔍</span>
            <span>AuditFlow ↗</span>
          </button>
          <button className="tp-nav-btn" onClick={() => navigate("suite")} type="button">
            <span className="tp-nav-icon">⬡</span>
            <span>Suite</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="tp-main">
        <div className="tp-topbar">
          <div className="tp-topbar-left">
            <button className="suite-home-trigger" onClick={() => navigate("suite")} type="button" aria-label="Open Ledzerly product suite" title="Back to Ledzerly Product Suite"><span /><span /><span /><span /></button>
            <span className="tp-topbar-title">
              {module === "dashboard" ? "Dashboard" : module === "itr" ? "ITR Filing" : module === "tds" ? "TDS / TCS Return" : "GST Returns"}
            </span>
            <span className="tp-topbar-fy">AY 2026-27</span>
          </div>
          <div className="tp-topbar-right">
            <button className="tp-topbar-icon-btn" type="button">🔔</button>
            <button className="tp-topbar-icon-btn" type="button">?</button>
            <div className="tp-topbar-avatar">{userName.slice(0, 2).toUpperCase()}</div>
            <div className="tp-topbar-user-info">
              <strong>{userName}</strong>
              <small>Firm Owner</small>
            </div>
          </div>
        </div>

        <div className="tp-content">
          {module === "dashboard" && <TaxPilotDashboard onStartItr={() => setModule("itr")} onStartTds={() => setModule("tds")} onStartGst={() => setModule("gst")} />}
          {module === "itr" && <ItrClientFlow onBack={() => setModule("dashboard")} />}
          {module === "tds" && <TdsClientFlow onBack={() => setModule("dashboard")} />}
          {module === "gst" && <GstReturnsView onBack={() => setModule("dashboard")} />}
        </div>
      </div>
    </div>
  );
}
